import { Box } from '@mui/material';
import { lazy, Suspense, useEffect } from 'react';
import type { DataPoint } from '@/features/chartsView';
import { LineChartWrapper } from '@/features/chartsView';
import { useDataFetcher } from '@/shared/hooks';
import { useAppSelector } from '@/shared/model';
import { isDateDimension, isMediaDimension } from '@/shared/types';
import { Loader } from '@/shared/ui';
import { CHART_BASE_DIMENSION } from '../../model/constants';
import { selectMeta } from '../../model/slice';
import type { DataConfig, ROIBreakdownDataResponse } from '../../model/types';
import { useChartParams } from './useChartParams';

const AreaChart = lazy(() =>
  import('./AreaChart').then(({ AreaChart }) => {
    return {
      default: AreaChart,
    };
  }),
);

interface Props {
  config: DataConfig;
  onGetData: () => Promise<ROIBreakdownDataResponse>;
  onDataChange?: (data?: ROIBreakdownDataResponse) => void;
}

export function Chart({ config, onGetData, onDataChange }: Readonly<Props>) {
  const meta = useAppSelector(selectMeta);
  const dimensionIsMedia = meta
    ? isMediaDimension(meta.config, config.dimension)
    : false;
  const dimensionIsDate = meta
    ? isDateDimension(meta.config, config.dimension)
    : false;

  const { data, dataStatus } = useDataFetcher(onGetData);

  const splitOptions = data?.splitOptions?.[config.dimension] || [];
  const { noData, lines, yAxis, data: chartData } = useChartParams({
    config: meta?.config,
    metric: config.metric,
    data: (data?.data || []) as DataPoint<typeof CHART_BASE_DIMENSION>[],
    splitOptions,
    excludedSplitOptions: config.excludedSplitOptions,
  });

  useEffect(() => {
    onDataChange?.(data ?? undefined);
  }, [data, onDataChange]);

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <LineChartWrapper
        dataStatus={dataStatus}
        noData={noData}
        noSelection={lines.length === config.excludedSplitOptions.length}
      >
        <Suspense fallback={<Loader active />}>
          <AreaChart
            excludedLines={config.excludedSplitOptions}
            isMediaContent={dimensionIsMedia}
            isDateContent={dimensionIsDate}
            xAxisDataKey={CHART_BASE_DIMENSION}
            yAxis={yAxis!}
            data={chartData}
            lines={lines}
            isRelative={config.showRelativeValues}
          />
        </Suspense>
      </LineChartWrapper>
    </Box>
  );
}
